import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { submitQuizResult } from '../services/quizApi';
import { useQuizSummary } from '../context/QuizSummaryContext';

const sampleQuestions = [
  {
    question: "What is the primary language of the Vedas?",
    options: ["Sanskrit", "Hindi", "Tamil", "English"],
    answer: "Sanskrit"
  },
  {
    question: "How many Vedas are there?",
    options: ["One", "Two", "Three", "Four"],
    answer: "Four"
  },
  {
    question: "Which of the following is not a Veda?",
    options: ["Rigveda", "Yajurveda", "Atharvaveda", "Ayurveda"],
    answer: "Ayurveda"
  }
];

const Quiz = ({ questions = sampleQuestions }) => {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const { refreshSummary } = useQuizSummary();

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  const [score, setScore] = useState(0);
  const [showScore, setShowScore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    if (showScore && user) {
      const submitResult = async () => {
        setLoading(true);
        setError(null);
        
        const totalMarks = questions.length;
        const percentage = totalMarks > 0 ? (score / totalMarks) * 100 : 0;
        const points = score; // 1 point per correct answer

        const resultData = {
          userId: user.sub,
          moduleId: moduleId,
          result: score,
          totalMarks: totalMarks,
          percentage: Math.round(percentage),
          points: points,
          takenAt: new Date().toISOString(),
        };

        try {
          await submitQuizResult(resultData);
          refreshSummary();
        } catch (err) {
          setError('Failed to submit quiz results. Please try again.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      submitResult();
    }
    else if (showScore && !user) {
        setError("You must be logged in to save your score.");
    }
  }, [showScore, score, questions.length, moduleId, user, refreshSummary]);

  if (questions.length === 0) {
    return <div style={{fontFamily: 'Lato, sans-serif', textAlign: 'center'}}>
      <h2 style={{fontFamily: 'Playfair Display, serif', color: '#1F2937'}}>Quiz coming soon!</h2>
    </div>;
  }

  const handleOptionChange = (e) => {
    if (!isAnswered) {
      setSelectedOption(e.target.value);
    }
  };

  const handleCheckAnswer = () => {
    if (selectedOption === questions[currentQuestion].answer) {
      setScore(score + 1);
      setIsCorrect(true);
    } else {
      setIsCorrect(false);
    }
    setIsAnswered(true);
  };

  const handleNextQuestion = () => {
    setSelectedOption('');
    setIsAnswered(false);
    setIsCorrect(false);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowScore(true);
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setScore(0);
    setShowScore(false);
    setIsAnswered(false);
    setIsCorrect(false);
    setSelectedOption('');
  };

  const buttonStyle = {
    background: '#D97706',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    marginTop: '20px'
  };

  return (
    <div className="quiz-container" style={{fontFamily: 'Lato, sans-serif'}}>
      {showScore ? (
        <div className="score-section" style={{textAlign: 'center'}}>
          <h2 style={{fontFamily: 'Playfair Display, serif', color: '#1F2937'}}>Your score: {score}/{questions.length}</h2>
          {loading && <p>Submitting results...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          <button onClick={restartQuiz} style={buttonStyle}>Restart Quiz</button>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', padding: '10px', background: '#f3f4f6', borderRadius: '8px' }}>
             <span style={{ fontWeight: 'bold', color: '#059669' }}>Correct Answers: {score}</span>
             <span style={{ fontWeight: 'bold', color: '#4B5563' }}>Questions Left: {questions.length - currentQuestion - (isAnswered ? 1 : 0)}</span>
          </div>
          <div className="question-section">
            <h2 style={{fontFamily: 'Playfair Display, serif', color: '#1F2937', marginBottom: '20px'}}>{questions[currentQuestion].question}</h2>
          </div>
          <div className="options-section">
            {questions[currentQuestion].options.map((option) => (
              <div key={option} style={{marginBottom: '10px'}}>
                <input
                  type="radio"
                  id={option}
                  name="option"
                  value={option}
                  checked={selectedOption === option}
                  onChange={handleOptionChange}
                  disabled={isAnswered}
                />
                <label htmlFor={option} style={{marginLeft: '10px'}}>{option}</label>
              </div>
            ))}
          </div>

          {isAnswered && (
            <div style={{ marginTop: '20px', padding: '15px', borderRadius: '8px', backgroundColor: isCorrect ? '#D1FAE5' : '#FEE2E2', border: `1px solid ${isCorrect ? '#10B981' : '#EF4444'}` }}>
              {isCorrect ? (
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{ color: '#065F46', margin: 0 }}>🎉 Excellent! Correct Answer! 🌟</h3>
                  <p style={{ color: '#047857', margin: '5px 0 0' }}>Keep up the great work!</p>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{ color: '#991B1B', margin: 0 }}>❌ Oops! Incorrect.</h3>
                  <p style={{ color: '#B91C1C', margin: '5px 0 0' }}>The correct answer was: <strong>{questions[currentQuestion].answer}</strong></p>
                </div>
              )}
            </div>
          )}

          <button onClick={isAnswered ? handleNextQuestion : handleCheckAnswer} disabled={!selectedOption} style={buttonStyle}>
            {isAnswered ? (currentQuestion < questions.length - 1 ? "Next Question" : "Finish Quiz") : "Check Answer"}
          </button>
        </>
      )}
    </div>
  );
};

export default Quiz;